import { useState } from "react";
import { useT } from "../i18n/hooks.ts";
import type { TextKey } from "../i18n/es.ts";
import { LegendIcon } from "./LegendIcon.tsx";
import { saveLegendHidden, readLegendHidden } from "../model/mapLegend.ts";
import {
  AQUIFER_STYLE,
  BASE_STYLE,
  PROBLEM_STYLE,
  OUTLET_POINT_STYLE,
  UNLINKED_STYLE,
} from "./mapStyles.ts";

interface SampleStyle {
  color: string;
  fillColor: string;
  fillOpacity: number;
  dashArray?: string;
}

const ITEMS: {
  id: string;
  style: SampleStyle;
  shape: "square" | "circle";
  label: TextKey;
  /** Only for "Sub-basin with snow": references SnowPatterns's SVG pattern (SubBasinMap.tsx) instead of a solid fill. */
  patternId?: string;
}[] = [
  { id: "normal", style: BASE_STYLE, shape: "square", label: "legend.normalSubBasin" },
  {
    id: "snow",
    style: BASE_STYLE,
    shape: "square",
    label: "legend.subBasinWithSnow",
    patternId: "pattern-snow-normal",
  },
  {
    id: "issues",
    style: PROBLEM_STYLE,
    shape: "square",
    label: "legend.subBasinWithIssues",
  },
  {
    id: "unlinked",
    style: UNLINKED_STYLE,
    shape: "square",
    label: "legend.unlinked",
  },
  {
    id: "outletPoint",
    style: OUTLET_POINT_STYLE,
    shape: "circle",
    label: "legend.outletPoint",
  },
  { id: "aquifer", style: AQUIFER_STYLE, shape: "square", label: "legend.aquifer" },
];

function Sample({ style, shape }: { style: SampleStyle; shape: "square" | "circle" }) {
  return (
    <span
      className="legend-sample"
      style={{
        borderColor: style.color,
        backgroundColor: style.fillColor,
        opacity: Math.max(style.fillOpacity, 0.55), // at this small a scale, the map's real opacity is barely visible
        borderRadius: shape === "circle" ? "50%" : "3px",
        borderStyle: style.dashArray ? "dashed" : "solid",
      }}
    />
  );
}

/**
 * Same as `Sample`, but for the "with snow zone" item: the stripe pattern
 * is an SVG `fill` (see SnowPatterns in SubBasinMap.tsx), and `fill` as a
 * CSS property only has an effect on SVG elements — a regular `<span>`
 * can't show it, so this sample is an `<svg>` instead of inheriting the
 * same `<span>` as the rest of the legend.
 */
function PatternSample({ color, patternId }: { color: string; patternId: string }) {
  return (
    <svg className="legend-sample" viewBox="0 0 12 12" aria-hidden="true">
      <rect
        x="0.75"
        y="0.75"
        width="10.5"
        height="10.5"
        rx="1.5"
        fill={`url(#${patternId})`}
        stroke={color}
        strokeWidth="1.5"
      />
    </svg>
  );
}

/**
 * Map color legend (bottom-left corner, clear of Leaflet's controls —
 * zoom, top-left — and the drawing bar — bottom-centered, see
 * MapDrawing.tsx). Can be hidden, and the choice is remembered across
 * sessions (mapLegend.ts) — hiding it leaves a small button to bring it
 * back, it doesn't disappear without a trace.
 */
export function Legend() {
  const t = useT();
  const [visible, setVisible] = useState(() => !readLegendHidden());

  function toggle(newVisible: boolean) {
    setVisible(newVisible);
    saveLegendHidden(!newVisible);
  }

  if (!visible) {
    return (
      <button
        className="button-icon legend-reopen-button"
        onClick={() => toggle(true)}
        aria-label={t("legend.show")}
        title={t("legend.show")}
      >
        <LegendIcon />
      </button>
    );
  }

  return (
    <div className="map-legend">
      <div className="legend-header">
        <strong>{t("legend.title")}</strong>
        <button
          className="button-icon"
          onClick={() => toggle(false)}
          aria-label={t("legend.hide")}
          title={t("legend.hide")}
        >
          ✕
        </button>
      </div>
      <ul>
        {ITEMS.map((item) => (
          <li key={item.id}>
            {item.patternId ? (
              <PatternSample color={item.style.color} patternId={item.patternId} />
            ) : (
              <Sample style={item.style} shape={item.shape} />
            )}
            {t(item.label)}
          </li>
        ))}
      </ul>
    </div>
  );
}
